import { jsx } from "react/jsx-runtime";
import { css } from "@/styled-system/css";
import { Paragraph } from "@zillow/constellation-10";
import { usePageProps, migrateColorToken, resolveTextAlign, resolveSpacing, migrateTypographyToken, v8ResponsiveToPanda } from "@zillow/zrs-cms";
import { pickAnimationDomProps } from "../../components/BlockAnimation/animationConfig.esm.js";
import { usePlacement } from "../../components/PlacementProvider/PlacementProvider.esm.js";
import makeContentInteractive from "../../util/makeSpansInteractive.esm.js";
const TEXT_STYLE_TO_FONT_PROPS = {
  "body-lg": {
    fontSize: "body-lg",
    lineHeight: "body-lg"
  },
  body: {
    fontSize: "body",
    lineHeight: "body"
  },
  "body-sm": {
    fontSize: "body-sm",
    lineHeight: "body-sm"
  },
  "body-xs": {
    fontSize: "body-xs",
    lineHeight: "body-xs"
  },
  fineprint: {
    fontSize: "fineprint",
    lineHeight: "fineprint"
  },
  // Bold variants share the same fontSize/lineHeight tokens as their
  // non-bold counterparts — the textStyle prop carries the weight, the css
  // prop only needs to scale the size across breakpoints.
  "body-lg-bold": {
    fontSize: "body-lg",
    lineHeight: "body-lg"
  },
  "body-bold": {
    fontSize: "body",
    lineHeight: "body"
  },
  "body-sm-bold": {
    fontSize: "body-sm",
    lineHeight: "body-sm"
  },
  "body-xs-bold": {
    fontSize: "body-xs",
    lineHeight: "body-xs"
  },
  "fineprint-bold": {
    fontSize: "fineprint",
    lineHeight: "fineprint"
  }
};
function buildResponsiveFontCss(breakpointMap) {
  const fontSize = {};
  const lineHeight = {};
  Object.entries(breakpointMap).forEach(([pandaKey, textStyleValue]) => {
    const fontProps = TEXT_STYLE_TO_FONT_PROPS[textStyleValue];
    if (fontProps) {
      fontSize[pandaKey] = fontProps.fontSize;
      lineHeight[pandaKey] = fontProps.lineHeight;
    }
  });
  return {
    fontSize,
    lineHeight
  };
}
function clampToParagraphTextStyle(textStyle) {
  return typeof textStyle === "string" && textStyle.startsWith("heading-") ? "body" : textStyle;
}
const BlockParagraph = ({
  attrs
}) => {
  const {
    content,
    textAlign = "inherit",
    margin,
    fontType = "body",
    fontColor = "",
    marginBottom: attrMarginBottom = "default",
    marginTop: attrMarginTop,
    marginLeft: attrMarginLeft,
    marginRight: attrMarginRight
  } = attrs;
  const pageProps = usePageProps();
  const isPost = usePlacement() === "post";
  if (!content || !content.trim()) {
    return null;
  }
  const interactiveContent = makeContentInteractive(content, {
    pageProps
  });
  const effectiveMarginBottom = isPost ? "default" : (margin == null ? void 0 : margin.marginBottom) ?? attrMarginBottom;
  const effectiveMarginTop = isPost ? attrMarginTop : (margin == null ? void 0 : margin.marginTop) ?? attrMarginTop;
  const effectiveMarginLeft = (margin == null ? void 0 : margin.marginLeft) ?? attrMarginLeft;
  const effectiveMarginRight = (margin == null ? void 0 : margin.marginRight) ?? attrMarginRight;
  const scalarFontColor = typeof fontColor === "object" && fontColor !== null ? fontColor.default ?? "" : fontColor;
  const resolvedColor = scalarFontColor ? migrateColorToken(scalarFontColor) : void 0;
  const resolvedTextAlign = resolveTextAlign(textAlign);
  const resolvedMarginBottom = resolveSpacing(effectiveMarginBottom);
  const resolvedMarginTop = resolveSpacing(effectiveMarginTop);
  const resolvedMarginLeft = resolveSpacing(effectiveMarginLeft);
  const resolvedMarginRight = resolveSpacing(effectiveMarginRight);
  let resolvedTextStyle;
  let responsiveFontCss;
  if (typeof fontType === "object" && fontType !== null) {
    const keys = Object.keys(fontType);
    if (keys.length <= 1) {
      const baseValue = fontType.default ?? Object.values(fontType)[0];
      resolvedTextStyle = clampToParagraphTextStyle(migrateTypographyToken(baseValue));
    } else {
      const breakpointMap = v8ResponsiveToPanda(fontType, migrateTypographyToken);
      const base = breakpointMap.base ?? migrateTypographyToken(fontType.default ?? Object.values(fontType)[0]);
      resolvedTextStyle = clampToParagraphTextStyle(base);
      responsiveFontCss = buildResponsiveFontCss(breakpointMap);
    }
  } else {
    resolvedTextStyle = clampToParagraphTextStyle(migrateTypographyToken(fontType));
  }
  const cssProps = css.raw({
    color: resolvedColor ?? "inherit",
    ...resolvedMarginBottom !== void 0 && {
      marginBottom: resolvedMarginBottom
    },
    ...resolvedMarginTop !== void 0 && {
      marginTop: resolvedMarginTop
    },
    ...resolvedMarginLeft !== void 0 && {
      marginLeft: resolvedMarginLeft
    },
    ...resolvedMarginRight !== void 0 && {
      marginRight: resolvedMarginRight
    },
    ...resolvedTextAlign !== void 0 && {
      textAlign: resolvedTextAlign
    },
    ...responsiveFontCss && {
      fontSize: responsiveFontCss.fontSize,
      lineHeight: responsiveFontCss.lineHeight
    }
  });
  return /* @__PURE__ */ jsx(Paragraph, { textStyle: resolvedTextStyle, css: cssProps, ...pickAnimationDomProps(attrs), children: interactiveContent });
};
export {
  BlockParagraph as default
};
